<template>
    <div class="grid-layout">
        <div 
            v-if="$validateText(categoryData.cons) || $validateText(categoryData.pros)" 
            class="container pros-cons"
        >
            <div class="container-inner">
                <h3>{{ $translate('pros', 'Fördelar') }}</h3>
                <ul v-if="$validateText(categoryData.pros)">
                    <li
                        v-for="(pro, index) in categoryData.pros"
                        :key="index"
                    >
                        <i class="fa-regular fa-check fa-check1" />
                        <span>{{ $prismic.asText([pro]) }}</span>
                    </li>
                </ul>
            </div>

            <div class="container-inner">
                <h3>{{ $translate('cons', 'Nackdelar') }}</h3>
                <ul v-if="$validateText(categoryData.cons)">
                    <li
                        v-for="(con, index) in categoryData.cons"
                        :key="index"
                    >
                        <i class="fa-regular fa-xmark fa-clear" />
                        <span>{{ $prismic.asText([con]) }}</span>
                    </li>
                </ul>
            </div>
        </div>

        <div v-if="author" class="author-container">
            <h3>{{ $translate('what_we_think_of_the_card', 'Vad vi tycker om kortet') }}</h3>
            <div 
                class="rich-text"    
                v-html="$prismic.asHtml(document.data.our_opinion)"
            />
            <div class="author-wrapper">
                <prismic-image
                    h="50"
                    w="50" 
                    :img="author.data.image"
                />
                <div class="author-data">
                    <span>{{ author.data.name }}</span>
                    <span class="last-updated">
                        <i class="fa-regular fa-calendar" />
                        {{ $timestamp(document.last_publication_date) }}
                    </span>
                </div>
            </div>
        </div>

        <div
            v-if="showBonus.length"
            class="container data"
        >
            <h3>{{ $translate('bonus_and_benefits', 'Bonus & Förmåner') }}</h3>
            <ul>
                <li
                    v-for="(iterator, index) in bonus"
                    :key="index"
                >
                    <span>{{ iterator.text }}</span>
                    <span>
                        <i v-if="!iterator.value" class="fa-regular fa-xmark fa-clear" />
                        <i v-else class="fa-regular fa-check fa-check1" />
                    </span>
                </li>
            </ul>
            <span class="info-text">{{ $translate('read_more_about_the_card_bonus_and_benefits_below', 'Läs mer kring kortets bonus och förmåner nedan.') }}</span>
        </div>

        <div
            v-if="showInsurances.length"
            class="container data"
        >
            <h3>{{ $translate('insurances', 'Försäkringar') }}</h3>
            <ul>
                <li
                    v-for="(insurance, index) in insurances"
                    :key="index"
                >
                    <span>{{ insurance.text }}</span>
                    <span>
                        <i class="fa-regular fa-check fa-check1" />
                    </span>
                </li>
            </ul>

            <span class="info-text">{{ $translate('some_insurances_is_not_always_included', 'Observera att vissa försäkringar ibland inte ingår automatiskt.') }}</span>
        </div>

        <div class="container data">
            <h3>{{ $translate('credit_and_fees', 'Kredit & Avgifter') }}</h3>
            <card-info
                :show-header="false"
                :model="model"
            />
        </div>
        
        <div class="container data">
            <h3>{{ $translate('mobile_payments', 'Mobila betalningsmetoder') }}</h3>
            <mobile-payments
                :show-header="false"
                :model="model"
            />
        </div>

        <div class="container demands shadow-box">
            <div class="container-inner">
                <h3>{{ $translate('demands', 'Krav') }}</h3>
                <card-demands 
                    :show-header="false"
                    :model="model" 
                />
            </div>
        </div>
    </div>
</template>

<script>
import { orderBy } from 'lodash';
import CardInfo from '@/components/slices/list/list-item/list-item-components/CardInfo.vue';
import CardDemands from '@/components/slices/list/list-item/list-item-components/CardDemands.vue';
import MobilePayments from '@/components/slices/list/list-item/list-item-components/MobilePayments.vue';
export default {
    components: { 
        CardInfo, 
        CardDemands,
        MobilePayments,
    },
    props: {
        document: {
            type: Object,
            required: true,
            default: () => {}
        },
        model: {
            type: Object,
            required: true,
            default: () => {}
        }
    },
    computed: {
        categoryData() {
            return this.$getCategoryData(this.document);
        },
        showBonus() {
            return this.bonus.filter(bool => bool.value !== false);
        },
        showInsurances() {
            return this.insurances.filter(bool => bool.value !== false);
        },
        bonus() {
            let array = [
                {
                    text: this.$translate('bonus', 'Bonus'),
                    value: this.model.hasBonusProgram
                },
                {
                    text: this.$translate('concierge_service', 'Concierge tjänst'),
                    value: this.model.hasConciergeService
                },
                {
                    text: this.$translate('access_to_lounges', 'Tillgång till lounger'),
                    value: this.model.hasLoungeAccess
                },
                {
                    text: this.$translate('compare_cards_gives_cashback', 'Ger cashback'),
                    value: this.model.hasCashback
                }
            ];
            return orderBy(array.filter(item => item.value), 'text', 'asc');
        },
        insurances() {
            let array = [
                {
                    text: this.$translate('payment_protection', 'Betalskydd'),
                    value: this.model.hasPurchaseProtection
                },
                {
                    text: this.$translate('delivery_insurance', 'Leveransförsäkring'),
                    value: this.model.hasDeliveryInsurance
                },
                {
                    text: this.$translate('travel_insurance', 'Reseförsäkring'),
                    value: this.model.hasTravelInsurance
                },
                {
                    text: this.$translate('price_guarantee', 'Prisgaranti'),
                    value: this.model.hasPriceGuarantee
                },
                {
                    text: this.$translate('comprehensive_insurance', 'Allriskförsäkring'),
                    value: this.model.hasComprehensiveInsurance
                },
                {
                    text: this.$translate('cancellation_protection', 'Avbeställningsskydd'),
                    value: this.model.hasCancellationProtection
                },
                {
                    text: this.$translate('deductible_insurance', 'Självriskreducering'),
                    value: this.model.hasDeductibleInsurance
                },
                {
                    text: this.$translate('key_insurance', 'Nyckelförsäkring'),
                    value: this.model.hasLostKeyInsurance
                },
                {
                    text: this.$translate('wrong_fuel_insurance', 'Feltankningsförsäkring'),
                    value: this.model.hasWrongFuelInsurance
                },
                {
                    text: this.$translate('hole_in_one_insurance', 'Hole-in-one försäkring'),
                    value: this.model.hasHoleInOneInsurance
                },
                {
                    text: this.$translate('id_theft_insurance', 'Id-skyddsförsäkring'),
                    value: this.model.hasIdTheftInsurance
                }
            ];

            return orderBy(array.filter(item => item.value), 'text', 'asc');
        },
        author() {
            return this.$store.state.authors[this.document.data.author?.id];
        },
    }
};
</script>

<style lang="scss" scoped>

.container::v-deep {
    width: 100%;
    li,
    i { @include font-size(16px,8px); }

    &.pros-cons {
        @include grid(1,40px);
        @include device(pad) {
            @include grid(2,80px);
        }
        .container-inner {
            ul {
                @include grid(1,20px);
                li {
                    @include flex(start,start);
                    span {
                        flex: 1;
                        margin-left: 10px;
                    }
                    i { 
                        font-size: 17px; 
                        margin-top: 2px;
                    }

                }
            }
        }
    }

    &.demands {
        @include grid(1,40px);
        @include device(pad) {
            @include grid(2,80px);
        }
        li {
            @include font-size(16px,8px);
            @include flex(start,start);
            @include spacing(padding,2,(top,bottom));

            span { flex: 1; }
            i {
                font-size: 18px;
                margin-right: 10px;
            }
        }
    }
}
.author-container {
    @include spacing(padding,6);
    background-color: $graybg;
    @include border-radius(10px);

    .rich-text::v-deep {
        margin-bottom: 0 !important;
        > *:first-child {
            @include spacing(margin,0,top);
        }
    }

    .author-wrapper {
        display: flex; 
        align-items: center; 
        column-gap: 1rem; 
        @include spacing(margin,4,top);

        .author-data {
            display: flex; 
            flex-direction: column; 
            row-gap: 0.5rem;

            .last-updated {
                font-weight: lighter; 
                font-size: 0.875em;
            }
        }
    }
}

.shadow-box {
    box-shadow: $bxs;
    @include border-radius(25px);
    @include border;
    @include spacing(padding,6);
}
</style>
